/**
 * Typed façade over every gateway route. Pages call these instead of building URLs by hand.
 * Route list and query-string names come straight from the API route table.
 */

import { apiDelete, apiGet, apiPost, apiPut, apiUpload, buildQuery } from './api';
import type { UploadHandle } from './api';
import type {
  ApprovalStatus,
  DocumentStatus,
  GatewayScenario,
  QboEntityType,
  ScheduledPaymentStatus,
  SyncDirection,
  SyncOutcome,
  VendorInvoiceIntakeStage,
} from './enums';
import type {
  AcceptInvitationRequest,
  ActivateAccountResponse,
  AiSettingsDto,
  ApprovalDecisionRequest,
  ApprovalDto,
  ChangePasswordRequest,
  CreateInvitationRequest,
  CreateLicencePurchaseRequest,
  CreateLocalItemRequest,
  CreateUserRequest,
  CurrentUserDto,
  DashboardDto,
  DocumentDetailDto,
  DocumentOverrideRequest,
  DocumentSummaryDto,
  InvitationDto,
  InvitationPreviewDto,
  ItemPullResponse,
  LicencePackageCatalogDto,
  LicencePlanConfiguratorDto,
  LicencePurchaseResponseDto,
  LocalAccountDto,
  LocalItemDto,
  MatchPurchaseOrderRequest,
  PagedResult,
  PasswordPolicyDto,
  PostDocumentRequest,
  VendorIntakeApprovalRequest,
  VendorIntakeApprovalResponse,
  VendorIntakeApprovalRecordDto,
  VendorIntakeComparisonResponse,
  VendorIntakeHandoffResponse,
  PostDocumentResponse,
  PullRequest,
  PullResponse,
  PurchaseOrderMatchSuggestionDto,
  QboConnectStartResponse,
  QboConnectionDto,
  QboTransactionDto,
  QboVendorMatchDto,
  RejectDocumentRequest,
  ReferForApprovalRequest,
  ResendLicenceVerificationResponse,
  ResolveUnknownRequest,
  RequestInfoRequest,
  ResetPasswordRequest,
  ReviewClaimDto,
  CompanyAccessPolicyDto,
  UpdateCompanyAccessPolicyRequest,
  VendorInvoiceIntakeSummaryDto,
  VendorInvoiceIntakeDetailDto,
  IntakeLineReviewRequest,
  IntakeLineBatchReviewRequest,
  IntakeHeaderReviewRequest,
  IntakeTaxReviewDto,
  AcceptTaxDifferenceRequest,
  QboTaxCodeDto,
  PullTaxCodesResponse,
  QboTermDto,
  PullTermsResponse,
  SignInRequest,
  SignInResponse,
  SignUpRequest,
  SignUpResponse,
  SyncHistoryDto,
  PlatformStateDto,
  PlatformDeliveryDto,
  LicensePurchaseDto,
  PartnerPaymentNotificationDto,
  PartnerPaymentScanResponse,
  AssistantContextDto,
  AssistantAskRequest,
  AssistantReplyDto,
  SyncRuleDto,
  TenantOnboardingDto,
  TestSyncRuleRequest,
  TestSyncRuleResponse,
  UpdateAiSettingsRequest,
  UpdateUserRequest,
  UpsertSyncRuleRequest,
  UpsertVendorRequest,
  UserDto,
  VendorDto,
  // trading partners
  DirectoryEntryDto,
  HandshakeDeliveryDto,
  HandshakeExchangeStateDto,
  HandshakePullResponse,
  PartnerOutboundDeliveryDto,
  SendableInvoiceDto,
  DirectorySearchRequest,
  DirectorySearchResponse,
  PartnerConnectionDto,
  ProfileDraftDto,
  ProfileDraftEdit,
  RequestConnectionRequest,
  RespondConnectionRequest,
  TenantEntitlementsDto,
  // AI Learning
  LearningDiagnostics,
  EvidencePullApiRequest,
  EvidencePullApiResponse,
  EvidenceDeletionApiResponse,
  AttachmentDiscoveryApiResponse,
  AttachmentRetrievalApiResponse,
  PublicationStateDto,
  PublicationConsentApiRequest,
  PublishApiResponse,
  VendorLearningSummaryDto,
  VendorLearningDetailDto,
  VendorLearningConsentApiRequest,
  VendorPublishApiResponse,
  // Email intake
  EmailIntakeStatusDto,
  ActivateEmailIntakeRequest,
  DisableEmailIntakeRequest,
  EmailIntakeApprovedRuleDto,
  UpsertEmailIntakeRuleRequest,
  EmailIntakeRunNowResult,
  EmailIntakePendingSenderDto,
  DecideEmailIntakePendingSenderRequest,
  EmailIntakeForwarderDto,
  UpsertEmailIntakeForwarderRequest,
  EmailIntakeHeldInvoiceDto,
  ResolveHeldInvoiceRequest,
  EmailIntakeCompanyAliasDto,
  AddCompanyAliasRequest,
  SetCompanyMatchModeRequest,
  // Payables Agent
  PayablesAgentSetupDto,
  UpdatePayablesAgentSetupRequest,
  ScheduledPaymentDto,
  VendorPaymentProfileDto,
  VendorStageRow,
  AdvanceVendorStageRequest,
  SetVendorStageRequest,
  SetVendorExclusionRequest,
  SetVendorOverrideDaysRequest,
  PayablesAgentLogEntryDto,
  PayablesAgentRunNowResult,
} from './types';

export interface PageParams {
  page?: number;
  pageSize?: number;
}

// ---------------------------------------------------------------------------
// Auth and users
// ---------------------------------------------------------------------------

export const auth = {
  signIn: (body: SignInRequest, signal?: AbortSignal) =>
    apiPost<SignInResponse>('/auth/signin', body, signal),

  me: (signal?: AbortSignal) => apiGet<CurrentUserDto>('/auth/me', signal),
};

/**
 * Anonymous self-service signup. None of these calls carry a session, so they are safe to make
 * from the public pages.
 */
export const signup = {
  create: (body: SignUpRequest, signal?: AbortSignal) =>
    apiPost<SignUpResponse>('/signup', body, signal),

  resend: (email: string, signal?: AbortSignal) =>
    apiPost<SignUpResponse>('/signup/resend', { email }, signal),

  activate: (token: string, signal?: AbortSignal) =>
    apiPost<ActivateAccountResponse>('/signup/activate', { token }, signal),

  passwordPolicy: (signal?: AbortSignal) =>
    apiGet<PasswordPolicyDto>('/signup/password-policy', signal),
};

/** Password recovery (anonymous) and password change (authenticated). */
export const account = {
  /** Always accepted (202) so the response cannot reveal whether the address exists. */
  forgotPassword: (email: string, signal?: AbortSignal) =>
    apiPost<void>('/account/forgot-password', { email }, signal),

  resetPassword: (body: ResetPasswordRequest, signal?: AbortSignal) =>
    apiPost<void>('/account/reset-password', body, signal),

  changePassword: (body: ChangePasswordRequest, signal?: AbortSignal) =>
    apiPost<void>('/account/change-password', body, signal),
};

export const invitations = {
  /** Anonymous: what the invitee is being invited to. */
  preview: (token: string, signal?: AbortSignal) =>
    apiGet<InvitationPreviewDto>(`/invitations/preview${buildQuery({ token })}`, signal),

  /** Anonymous: creates the user and returns a session. */
  accept: (body: AcceptInvitationRequest, signal?: AbortSignal) =>
    apiPost<SignInResponse>('/invitations/accept', body, signal),

  list: (signal?: AbortSignal) => apiGet<InvitationDto[]>('/invitations', signal),

  create: (body: CreateInvitationRequest, signal?: AbortSignal) =>
    apiPost<InvitationDto>('/invitations', body, signal),

  resend: (id: string, signal?: AbortSignal) =>
    apiPost<void>(`/invitations/${encodeURIComponent(id)}/resend`, undefined, signal),

  /** Revokes the invitation; the emailed link stops working immediately. */
  revoke: (id: string, signal?: AbortSignal) =>
    apiDelete<void>(`/invitations/${encodeURIComponent(id)}`, signal),
};

/** First-run checklist, provisioning progress and licence/quota state for the current tenant. */
export const onboarding = {
  /** refresh=true asks the server to poll the Registration Server first (rate limited there). */
  get: (signal?: AbortSignal, refresh = false) =>
    apiGet<TenantOnboardingDto>(refresh ? '/onboarding?refresh=true' : '/onboarding', signal),

  /** Asks the Registration Server for a fresh licence-verification email. Admin only. */
  resendVerification: (signal?: AbortSignal) =>
    apiPost<ResendLicenceVerificationResponse>('/onboarding/resend-verification', undefined, signal),
};

export const users = {
  list: (signal?: AbortSignal) => apiGet<UserDto[]>('/users', signal),

  create: (body: CreateUserRequest, signal?: AbortSignal) =>
    apiPost<UserDto>('/users', body, signal),

  update: (id: string, body: UpdateUserRequest, signal?: AbortSignal) =>
    apiPut<UserDto>(`/users/${encodeURIComponent(id)}`, body, signal),
};

// ---------------------------------------------------------------------------
// QuickBooks connection
// ---------------------------------------------------------------------------

export const qbo = {
  connection: (signal?: AbortSignal) => apiGet<QboConnectionDto>('/qbo/connection', signal),

  connect: (returnUrl: string, signal?: AbortSignal) =>
    apiPost<QboConnectStartResponse>('/qbo/connect', { returnUrl }, signal),

  disconnect: (signal?: AbortSignal) => apiPost<void>('/qbo/disconnect', undefined, signal),

  test: (signal?: AbortSignal) => apiPost<QboConnectionDto>('/qbo/test', undefined, signal),
};

// ---------------------------------------------------------------------------
// Connector-scoped QuickBooks Chart of Accounts
// ---------------------------------------------------------------------------

export interface LearningAccountListParams extends PageParams {
  search?: string | null;
  eligibleOnly?: boolean;
}

export const learningAccounts = {
  list: (params: LearningAccountListParams, signal?: AbortSignal) =>
    apiGet<PagedResult<LocalAccountDto>>(
      `/learning/accounts${buildQuery({
        search: params.search?.trim() || null,
        eligibleOnly: params.eligibleOnly ?? false,
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 100,
      })}`,
      signal,
    ),

  /** Pull the complete active/current account universe once; most companies complete in one request. */
  allEligible: async (signal?: AbortSignal): Promise<LocalAccountDto[]> => {
    const items: LocalAccountDto[] = [];
    let page = 1;

    for (;;) {
      const batch = await apiGet<PagedResult<LocalAccountDto>>(
        `/learning/accounts${buildQuery({ eligibleOnly: true, page, pageSize: 500 })}`,
        signal,
      );

      items.push(...batch.items);
      if (items.length >= batch.totalCount || batch.items.length === 0) return items;
      page += 1;
    }
  },
};

// ---------------------------------------------------------------------------
// Connector-scoped QuickBooks item (Product/Service) master
// ---------------------------------------------------------------------------

export interface LearningItemListParams extends PageParams {
  search?: string | null;
  eligibleOnly?: boolean;
}

export const learningItems = {
  list: (params: LearningItemListParams, signal?: AbortSignal) =>
    apiGet<PagedResult<LocalItemDto>>(
      `/learning/items${buildQuery({
        search: params.search?.trim() || null,
        eligibleOnly: params.eligibleOnly ?? false,
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 100,
      })}`,
      signal,
    ),

  /** Pull the complete active/current item universe once; most companies complete in one request. */
  allEligible: async (signal?: AbortSignal): Promise<LocalItemDto[]> => {
    const items: LocalItemDto[] = [];
    let page = 1;

    for (;;) {
      const batch = await apiGet<PagedResult<LocalItemDto>>(
        `/learning/items${buildQuery({ eligibleOnly: true, page, pageSize: 500 })}`,
        signal,
      );

      items.push(...batch.items);
      if (items.length >= batch.totalCount || batch.items.length === 0) return items;
      page += 1;
    }
  },

  /** Full read of the QuickBooks item master, same shape as the Chart of Accounts' full reconcile. */
  pull: (signal?: AbortSignal) => apiPost<ItemPullResponse>('/learning/items/pull', {}, signal),

  /** Creates a purchase-eligible Item in QuickBooks (Service or NonInventory only) and caches it
   * locally in one call — for a line whose item reference has no match in the item master yet. */
  create: (body: CreateLocalItemRequest, signal?: AbortSignal) =>
    apiPost<LocalItemDto>('/learning/items', body, signal),
};

// ---------------------------------------------------------------------------
// Registration Server licence catalogue, relayed through iERP AI
// ---------------------------------------------------------------------------

export interface LicenceCatalogParams {
  currencyCode?: string | null;
  billingPeriod?: 'MONTH' | 'YEAR';
  aiPowerFilter?: string | null;
}

export const licensing = {
  packageOffers: (params: LicenceCatalogParams = {}, signal?: AbortSignal) =>
    apiGet<LicencePackageCatalogDto>(
      `/licensing/package-offers${buildQuery({
        currencyCode: params.currencyCode ?? null,
        billingPeriod: params.billingPeriod ?? 'MONTH',
        aiPowerFilter: params.aiPowerFilter ?? 'RECOMMENDED',
      })}`,
      signal,
    ),

  configurator: (params: Pick<LicenceCatalogParams, 'currencyCode'> = {}, signal?: AbortSignal) =>
    apiGet<LicencePlanConfiguratorDto>(
      `/licensing/package-configurator${buildQuery({
        currencyCode: params.currencyCode ?? null,
      })}`,
      signal,
    ),

  purchase: (body: CreateLicencePurchaseRequest, signal?: AbortSignal) =>
    apiPost<LicencePurchaseResponseDto>('/licensing/purchases', body, signal),
};

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------

export const dashboard = {
  get: (signal?: AbortSignal) => apiGet<DashboardDto>('/dashboard', signal),
};

// ---------------------------------------------------------------------------
// Documents
// ---------------------------------------------------------------------------

export interface DocumentListParams extends PageParams {
  status?: DocumentStatus | null;
  scenario?: GatewayScenario | null;
  search?: string | null;
  /** Narrows the queue to one supplier - what a vendor card asks for. */
  qboVendorId?: string | null;
}

export interface UploadFields {
  scenario: GatewayScenario;
  targetQboEntityId?: string | null;
  qboVendorId?: string | null;
  notes?: string | null;
}

export const documents = {
  list: (params: DocumentListParams, signal?: AbortSignal) =>
    apiGet<PagedResult<DocumentSummaryDto>>(
      `/documents${buildQuery({
        status: params.status ?? null,
        scenario: params.scenario ?? null,
        search: params.search ?? null,
        qboVendorId: params.qboVendorId ?? null,
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 25,
      })}`,
      signal,
    ),

  get: (id: string, signal?: AbortSignal) =>
    apiGet<DocumentDetailDto>(`/documents/${encodeURIComponent(id)}`, signal),

  /** Path of the raw file endpoint; fetched through `fetchBlobUrl` so the token can be attached. */
  filePath: (id: string) => `/documents/${encodeURIComponent(id)}/file`,

  upload: (
    file: File,
    fields: UploadFields,
    onProgress?: (percent: number) => void,
  ): UploadHandle<DocumentSummaryDto> => {
    const form = new FormData();
    form.append('file', file, file.name);
    form.append('scenario', String(fields.scenario));
    if (fields.targetQboEntityId) form.append('targetQboEntityId', fields.targetQboEntityId);
    if (fields.qboVendorId) form.append('qboVendorId', fields.qboVendorId);
    if (fields.notes) form.append('notes', fields.notes);
    return apiUpload<DocumentSummaryDto>('/documents', form, onProgress);
  },

  /**
   * Approves a vendor intake: creates or adopts the QuickBooks vendor, then teaches iERP AI what
   * this supplier's invoices look like. The only route that turns an intake into learning.
   */
  approveVendorIntake: (id: string, body: VendorIntakeApprovalRequest, signal?: AbortSignal) =>
    apiPost<VendorIntakeApprovalResponse>(
      `/documents/${encodeURIComponent(id)}/vendor-intake/approve`, body, signal),

  /**
   * What approving this intake would CHANGE, field by field: the live QuickBooks value beside the
   * document's suggestion. Read live, because the approval writes against a SyncToken fetched at
   * that moment and a synchronised copy could mis-state what is about to be overwritten.
   */
  vendorIntakeComparison: (id: string, qboVendorId: string | null, signal?: AbortSignal) =>
    apiGet<VendorIntakeComparisonResponse>(
      `/documents/${encodeURIComponent(id)}/vendor-intake/comparison`
      + (qboVendorId ? `?qboVendorId=${encodeURIComponent(qboVendorId)}` : ''), signal),

  /**
   * Hands this invoice to vendor intake because its supplier does not exist yet. The SAME document
   * continues - same row, same file, same audit trail - and returns to being this invoice once the
   * supplier is approved.
   */
  startVendorIntake: (id: string, signal?: AbortSignal) =>
    apiPost<VendorIntakeHandoffResponse>(
      `/documents/${encodeURIComponent(id)}/vendor-intake/start`, {}, signal),

  /**
   * Re-reads a FINISHED vendor intake as the bill for the same invoice, over the same stored file.
   * Not a second upload: uploading the invoice again is what duplicate blocking exists to refuse,
   * and it would give one invoice two document rows and two idempotency identities.
   */
  createBillFromIntake: (id: string, signal?: AbortSignal) =>
    apiPost<VendorIntakeHandoffResponse>(
      `/documents/${encodeURIComponent(id)}/create-bill`, {}, signal),

  /**
   * What a person actually approved on this document's vendor intake — the proposal, the
   * QuickBooks values it would have replaced, what they chose, what QuickBooks ended up holding,
   * and what was learned. Still answerable after the resumed invoice's extraction has replaced the
   * contract the decision was made against, because it was never stored on the document.
   */
  vendorIntakeApprovalRecord: (id: string, signal?: AbortSignal) =>
    apiGet<VendorIntakeApprovalRecordDto>(
      `/documents/${encodeURIComponent(id)}/vendor-intake/approval`, signal),

  post: (id: string, body: PostDocumentRequest, signal?: AbortSignal) =>
    apiPost<PostDocumentResponse>(`/documents/${encodeURIComponent(id)}/post`, body, signal),

  reject: (id: string, body: RejectDocumentRequest, signal?: AbortSignal) =>
    apiPost<void>(`/documents/${encodeURIComponent(id)}/reject`, body, signal),

  refer: (id: string, body: ReferForApprovalRequest, signal?: AbortSignal) =>
    apiPost<ApprovalDto>(`/documents/${encodeURIComponent(id)}/refer`, body, signal),

  retry: (id: string, signal?: AbortSignal) =>
    apiPost<void>(`/documents/${encodeURIComponent(id)}/retry`, undefined, signal),

  cancel: (id: string, signal?: AbortSignal) =>
    apiPost<void>(`/documents/${encodeURIComponent(id)}/cancel`, undefined, signal),

  /**
   * Saves an operator edit.
   *
   * `expectedRevision` is the revision the screen was built from, and the API refuses the save with
   * code `StaleRevision` when the document has moved on. Always send it: without it, two reviewers
   * on one invoice means the second save silently overwrites the first person's corrections.
   */
  saveOverrides: (
    id: string,
    body: DocumentOverrideRequest,
    expectedRevision?: number | null,
    signal?: AbortSignal,
  ) =>
    apiPut<void>(
      `/documents/${encodeURIComponent(id)}/overrides`
      + buildQuery({ expectedRevision: expectedRevision ?? null }),
      body,
      signal,
    ),

  /**
   * Takes the review claim, so a colleague opening the same invoice is told somebody is already on
   * it rather than silently overwriting them later.
   *
   * A claim grants NOTHING except that courtesy: it is not read by the posting gate, the approval
   * limit check or the approval-required rule, and holding one does not make anybody an approver.
   */
  claim: (id: string, signal?: AbortSignal) =>
    apiPost<ReviewClaimDto>(`/documents/${encodeURIComponent(id)}/claim`, undefined, signal),

  release: (id: string, signal?: AbortSignal) =>
    apiPost<ReviewClaimDto>(`/documents/${encodeURIComponent(id)}/release`, undefined, signal),

  // Re-run reconciliation for a document whose posting outcome is unknown ("refresh reconciliation").
  reconcile: (id: string, signal?: AbortSignal) =>
    apiPost<DocumentSummaryDto>(`/documents/${encodeURIComponent(id)}/reconcile`, undefined, signal),

  // Administrative resolution of a quarantined document: confirm it posted, or release it to repost.
  resolveUnknown: (id: string, body: ResolveUnknownRequest, signal?: AbortSignal) =>
    apiPost<DocumentSummaryDto>(`/documents/${encodeURIComponent(id)}/resolve-unknown`, body, signal),
};

// ---------------------------------------------------------------------------
// Vendors
// ---------------------------------------------------------------------------

export interface VendorListParams extends PageParams {
  search?: string | null;
}

export interface VendorMatchParams {
  name?: string | null;
  taxId?: string | null;
  email?: string | null;
}

export const vendors = {
  list: (params: VendorListParams, signal?: AbortSignal) =>
    apiGet<PagedResult<VendorDto>>(
      `/vendors${buildQuery({
        search: params.search ?? null,
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 25,
      })}`,
      signal,
    ),

  create: (body: UpsertVendorRequest, signal?: AbortSignal) =>
    apiPost<VendorDto>('/vendors', body, signal),

  update: (qboVendorId: string, body: UpsertVendorRequest, signal?: AbortSignal) =>
    apiPut<VendorDto>(`/vendors/${encodeURIComponent(qboVendorId)}`, body, signal),

  pull: (signal?: AbortSignal) => apiPost<PullResponse>('/vendors/pull', undefined, signal),

  match: (params: VendorMatchParams, signal?: AbortSignal) =>
    apiGet<QboVendorMatchDto>(
      `/vendors/match${buildQuery({
        name: params.name ?? null,
        taxId: params.taxId ?? null,
        email: params.email ?? null,
      })}`,
      signal,
    ),
};

// ---------------------------------------------------------------------------
// Transactions
// ---------------------------------------------------------------------------

export interface TransactionListParams extends PageParams {
  entityType?: QboEntityType | null;
  search?: string | null;
}

export const transactions = {
  list: (params: TransactionListParams, signal?: AbortSignal) =>
    apiGet<PagedResult<QboTransactionDto>>(
      `/transactions${buildQuery({
        entityType: params.entityType ?? null,
        search: params.search ?? null,
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 25,
      })}`,
      signal,
    ),

  pull: (body: PullRequest, signal?: AbortSignal) =>
    apiPost<PullResponse>('/transactions/pull', body, signal),
};

// ---------------------------------------------------------------------------
// Purchase orders
// ---------------------------------------------------------------------------

export const purchaseOrders = {
  suggestions: (documentId: string, signal?: AbortSignal) =>
    apiGet<PurchaseOrderMatchSuggestionDto[]>(
      `/purchase-orders/suggestions${buildQuery({ documentId })}`,
      signal,
    ),

  match: (body: MatchPurchaseOrderRequest, signal?: AbortSignal) =>
    apiPost<PostDocumentResponse>('/purchase-orders/match', body, signal),
};

// ---------------------------------------------------------------------------
// Approvals
// ---------------------------------------------------------------------------

export interface ApprovalListParams extends PageParams {
  status?: ApprovalStatus | null;
}

export const approvals = {
  list: (params: ApprovalListParams, signal?: AbortSignal) =>
    apiGet<PagedResult<ApprovalDto>>(
      `/approvals${buildQuery({
        status: params.status ?? null,
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 25,
      })}`,
      signal,
    ),

  approve: (id: string, body: ApprovalDecisionRequest, signal?: AbortSignal) =>
    apiPost<void>(`/approvals/${encodeURIComponent(id)}/approve`, body, signal),

  reject: (id: string, body: ApprovalDecisionRequest, signal?: AbortSignal) =>
    apiPost<void>(`/approvals/${encodeURIComponent(id)}/reject`, body, signal),

  requestInfo: (id: string, body: RequestInfoRequest, signal?: AbortSignal) =>
    apiPost<void>(`/approvals/${encodeURIComponent(id)}/request-info`, body, signal),
};

// ---------------------------------------------------------------------------
// Sync rules
// ---------------------------------------------------------------------------

export const syncRules = {
  list: (signal?: AbortSignal) => apiGet<SyncRuleDto[]>('/sync-rules', signal),

  create: (body: UpsertSyncRuleRequest, signal?: AbortSignal) =>
    apiPost<SyncRuleDto>('/sync-rules', body, signal),

  update: (id: string, body: UpsertSyncRuleRequest, signal?: AbortSignal) =>
    apiPut<SyncRuleDto>(`/sync-rules/${encodeURIComponent(id)}`, body, signal),

  remove: (id: string, signal?: AbortSignal) =>
    apiDelete<void>(`/sync-rules/${encodeURIComponent(id)}`, signal),

  test: (body: TestSyncRuleRequest, signal?: AbortSignal) =>
    apiPost<TestSyncRuleResponse>('/sync-rules/test', body, signal),
};

// ---------------------------------------------------------------------------
// AI settings
// ---------------------------------------------------------------------------

export const aiSettings = {
  get: (signal?: AbortSignal) => apiGet<AiSettingsDto>('/ai-settings', signal),

  update: (body: UpdateAiSettingsRequest, signal?: AbortSignal) =>
    apiPut<AiSettingsDto>('/ai-settings', body, signal),
};

// ---------------------------------------------------------------------------
// Sync history
// ---------------------------------------------------------------------------

export interface HistoryListParams extends PageParams {
  outcome?: SyncOutcome | null;
  entityType?: QboEntityType | null;
  direction?: SyncDirection | null;
}

export const history = {
  list: (params: HistoryListParams, signal?: AbortSignal) =>
    apiGet<PagedResult<SyncHistoryDto>>(
      `/history${buildQuery({
        outcome: params.outcome ?? null,
        entityType: params.entityType ?? null,
        direction: params.direction ?? null,
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 25,
      })}`,
      signal,
    ),
};

export async function accountsApiRequest<T>(
  path: string,
  init?: RequestInit
): Promise<T> {
  const apiPath = path.startsWith("/api") ? path.substring(4) : path;
  const method = (init?.method ?? "GET").toUpperCase();
  const signal = init?.signal ?? undefined;

  if (method === "GET") {
    return apiGet<T>(apiPath, signal);
  }

  if (method === "POST") {
    let body: unknown = undefined;

    if (typeof init?.body === "string" && init.body.length > 0) {
      try {
        body = JSON.parse(init.body);
      } catch {
        body = init.body;
      }
    }

    return apiPost<T>(apiPath, body, signal);
  }

  throw new Error(`Unsupported Accounts API method: ${method}`);
}

// ---------------------------------------------------------------------------
// Company access policy
// ---------------------------------------------------------------------------

/**
 * The controls that exist because several people share one QuickBooks company. Anyone signed in may
 * READ them - a rule that constrains you is a rule you are entitled to see - and only an
 * administrator may change them.
 */
export const companyAccess = {
  get: (signal?: AbortSignal) =>
    apiGet<CompanyAccessPolicyDto>('/company/access-policy', signal),

  update: (body: UpdateCompanyAccessPolicyRequest, signal?: AbortSignal) =>
    apiPut<CompanyAccessPolicyDto>('/company/access-policy', body, signal),
};

// ---------------------------------------------------------------------------
// Vendor invoice intake
// ---------------------------------------------------------------------------

export interface VendorInvoiceIntakeListParams extends PageParams {
  stage?: VendorInvoiceIntakeStage | null;
  search?: string | null;
}

export const vendorInvoiceIntake = {
  list: (params: VendorInvoiceIntakeListParams, signal?: AbortSignal) =>
    apiGet<PagedResult<VendorInvoiceIntakeSummaryDto>>(
      `/vendor-invoice-intake${buildQuery({
        stage: params.stage ?? null,
        search: params.search ?? null,
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 25,
      })}`,
      signal,
    ),

  /**
   * One invoice, with its lines, its tax picture and everything that blocks it. Reading refreshes
   * the projection, so the screen can never show figures the last edit has already replaced.
   */
  get: (documentId: string, signal?: AbortSignal) =>
    apiGet<VendorInvoiceIntakeDetailDto>(
      `/vendor-invoice-intake/${encodeURIComponent(documentId)}`, signal),

  reviewHeader: (documentId: string, body: IntakeHeaderReviewRequest, signal?: AbortSignal) =>
    apiPut<void>(
      `/vendor-invoice-intake/${encodeURIComponent(documentId)}/header`, body, signal),

  /**
   * Records a decision about one line. The coding goes through the ordinary edit path, so the
   * revision bump, the fingerprint and the approval invalidation are identical to an edit made from
   * the invoice review pane.
   */
  reviewLine: (
    documentId: string,
    lineNo: number,
    body: IntakeLineReviewRequest,
    signal?: AbortSignal,
  ) =>
    apiPut<void>(
      `/vendor-invoice-intake/${encodeURIComponent(documentId)}/lines/${lineNo}`, body, signal),

  /**
   * Saves several lines' decisions together as one edit — one revision bump, one approval
   * supersession — instead of one of each per line. For a spreadsheet-style review where several
   * lines are corrected before saving.
   */
  reviewLinesBatch: (
    documentId: string,
    body: IntakeLineBatchReviewRequest,
    signal?: AbortSignal,
  ) =>
    apiPut<void>(
      `/vendor-invoice-intake/${encodeURIComponent(documentId)}/lines/batch`, body, signal),

  /**
   * Recomputes the tax from this company's codes. A recomputation WITHDRAWS any standing
   * acceptance, so it is a decision about the document in front of you rather than a refresh -
   * which is why it names the revision it was decided against.
   */
  recalculateTax: (
    documentId: string,
    expectedRevision?: number | null,
    signal?: AbortSignal,
  ) =>
    apiPost<IntakeTaxReviewDto>(
      `/vendor-invoice-intake/${encodeURIComponent(documentId)}/tax/recalculate`
      + buildQuery({ expectedRevision: expectedRevision ?? null }),
      undefined,
      signal,
    ),

  acceptTaxDifference: (
    documentId: string,
    body: AcceptTaxDifferenceRequest,
    signal?: AbortSignal,
  ) =>
    apiPost<IntakeTaxReviewDto>(
      `/vendor-invoice-intake/${encodeURIComponent(documentId)}/tax/accept`, body, signal),

  /** Fills in the per-line account suggestion from this company's own posting history. */
  recommend: (documentId: string, signal?: AbortSignal) =>
    apiPost<VendorInvoiceIntakeDetailDto>(
      `/vendor-invoice-intake/${encodeURIComponent(documentId)}/recommend`, undefined, signal),

  taxCodes: (signal?: AbortSignal) =>
    apiGet<QboTaxCodeDto[]>('/vendor-invoice-intake/tax-codes', signal),

  pullTaxCodes: (signal?: AbortSignal) =>
    apiPost<PullTaxCodesResponse>(
      '/vendor-invoice-intake/tax-codes/pull', undefined, signal),

  terms: (signal?: AbortSignal) =>
    apiGet<QboTermDto[]>('/vendor-invoice-intake/terms', signal),

  pullTerms: (signal?: AbortSignal) =>
    apiPost<PullTermsResponse>(
      '/vendor-invoice-intake/terms/pull', undefined, signal),
};

// ---------------------------------------------------------------------------
// Trading partners
// ---------------------------------------------------------------------------

export const partnerDirectory = {
  /** Every draft this company has, newest version first. */
  drafts: (signal?: AbortSignal) =>
    apiGet<ProfileDraftDto[]>('/partners/directory/drafts', signal),

  /** Builds a fresh draft FROM QuickBooks company information. Discloses nothing by itself. */
  buildDraft: (signal?: AbortSignal) =>
    apiPost<ProfileDraftDto>('/partners/directory/drafts', {}, signal),

  updateDraft: (draftId: string, body: ProfileDraftEdit, signal?: AbortSignal) =>
    apiPut<ProfileDraftDto>(`/partners/directory/drafts/${encodeURIComponent(draftId)}`, body, signal),

  /** Admin-only, and publishes NOTHING - it records that a person approved the disclosure. */
  approveDraft: (draftId: string, signal?: AbortSignal) =>
    apiPost<ProfileDraftDto>(
      `/partners/directory/drafts/${encodeURIComponent(draftId)}/approve`, {}, signal),

  /** The only call that discloses anything outside this company. */
  publishDraft: (draftId: string, signal?: AbortSignal) =>
    apiPost<ProfileDraftDto>(
      `/partners/directory/drafts/${encodeURIComponent(draftId)}/publish`, {}, signal),

  /** What the central directory currently holds for this company. 404 when never published. */
  central: (signal?: AbortSignal) =>
    apiGet<DirectoryEntryDto>('/partners/directory/central', signal),
};

export const partnerConnections = {
  search: (body: DirectorySearchRequest, signal?: AbortSignal) =>
    apiPost<DirectorySearchResponse>('/partners/connections/search', body, signal),

  /** Refreshes the local mirror from the central store, then lists it. */
  list: (signal?: AbortSignal) =>
    apiGet<PartnerConnectionDto[]>('/partners/connections', signal),

  request: (body: RequestConnectionRequest, signal?: AbortSignal) =>
    apiPost<PartnerConnectionDto>('/partners/connections/request', body, signal),

  respond: (connectionId: string, body: RespondConnectionRequest, signal?: AbortSignal) =>
    apiPost<PartnerConnectionDto>(
      `/partners/connections/${encodeURIComponent(connectionId)}/respond`, body, signal),

  revoke: (connectionId: string, message: string | null, signal?: AbortSignal) =>
    apiPost<PartnerConnectionDto>(
      `/partners/connections/${encodeURIComponent(connectionId)}/revoke`, { message }, signal),

  /** Binds the connection to the QuickBooks Vendor (buyer side) or Customer (seller side). */
  bind: (
    connectionId: string,
    body: { qboVendorId?: string | null; qboCustomerId?: string | null },
    signal?: AbortSignal,
  ) =>
    apiPost<PartnerConnectionDto>(
      `/partners/connections/${encodeURIComponent(connectionId)}/bind`, body, signal),

  /** Routes one invoice currency on this connection to a specific QBO vendor, overriding the
   * connection's bound vendor for that currency alone — see PartnerConnectionDto.currencyVendorLinks. */
  linkCurrencyVendor: (
    connectionId: string,
    body: { currencyCode: string; qboVendorId: string },
    signal?: AbortSignal,
  ) =>
    apiPost<PartnerConnectionDto>(
      `/partners/connections/${encodeURIComponent(connectionId)}/currency-vendor`, body, signal),

  /** Removes one currency's vendor override, leaving it to fall back to the bound vendor again. */
  unlinkCurrencyVendor: (connectionId: string, currencyCode: string, signal?: AbortSignal) =>
    apiDelete<PartnerConnectionDto>(
      `/partners/connections/${encodeURIComponent(connectionId)}/currency-vendor/${encodeURIComponent(currencyCode)}`,
      signal,
    ),

  /** SellerInvoiceSending is the seller's switch; BuyerInvoiceReceiving is the buyer's. */
  setCapability: (
    connectionId: string,
    capability: 'SellerInvoiceSending' | 'BuyerInvoiceReceiving',
    enabled: boolean,
    signal?: AbortSignal,
  ) =>
    apiPost<PartnerConnectionDto>(
      `/partners/connections/${encodeURIComponent(connectionId)}/capability`,
      { capability, enabled },
      signal,
    ),

  setAutoApply: (connectionId: string, enabled: boolean, signal?: AbortSignal) =>
    apiPost<PartnerConnectionDto>(
      `/partners/connections/${encodeURIComponent(connectionId)}/auto-apply`, { enabled }, signal),

  resendCompanyInfo: (connectionId: string, signal?: AbortSignal) =>
    apiPost<PartnerConnectionDto>(
      `/partners/connections/${encodeURIComponent(connectionId)}/company-info/resend`, {}, signal),

  broadcastCompanyInfo: (signal?: AbortSignal) =>
    apiPost<void>('/partners/connections/company-info/broadcast', {}, signal),

  /** Accepts or rejects a counterparty's pending company-information update. */
  respondCompanyInfo: (connectionId: string, accept: boolean, signal?: AbortSignal) =>
    apiPost<PartnerConnectionDto>(
      `/partners/connections/${encodeURIComponent(connectionId)}/company-info/respond`,
      { accept },
      signal,
    ),
};

export const partnerEntitlements = {
  /** Probed from iERP AI. Used to gate the UI honestly rather than guessing. */
  get: (signal?: AbortSignal) =>
    apiGet<TenantEntitlementsDto>('/partners/entitlements', signal),
};

export const partnerDeliveries = {
  list: (
    params: { status?: string | null; page?: number; pageSize?: number },
    signal?: AbortSignal,
  ) =>
    apiGet<PagedResult<HandshakeDeliveryDto>>(
      `/partners/deliveries${buildQuery({
        status: params.status ?? null,
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 50,
      })}`,
      signal,
    ),

  get: (id: string, signal?: AbortSignal) =>
    apiGet<HandshakeDeliveryDto>(`/partners/deliveries/${encodeURIComponent(id)}`, signal),

  /** Whether the channel is configured with iERP AI, and what the last pull said. */
  state: (signal?: AbortSignal) =>
    apiGet<HandshakeExchangeStateDto>('/partners/deliveries/state', signal),

  /** Configures the channel if needed, pulls a batch, stores and acknowledges it. */
  pull: (signal?: AbortSignal) =>
    apiPost<HandshakePullResponse>('/partners/deliveries/pull', {}, signal),

  /** Puts one Failed or Refused delivery back in line. The durable receipt makes it replay-safe. */
  retry: (id: string, signal?: AbortSignal) =>
    apiPost<void>(`/partners/deliveries/${encodeURIComponent(id)}/retry`, {}, signal),
};

export const partnerOutbound = {
  list: (
    params: { status?: string | null; connectionId?: string | null; page?: number; pageSize?: number },
    signal?: AbortSignal,
  ) =>
    apiGet<PagedResult<PartnerOutboundDeliveryDto>>(
      `/partners/outbound${buildQuery({
        status: params.status ?? null,
        connectionId: params.connectionId ?? null,
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 50,
      })}`,
      signal,
    ),

  /** The bound customer's QuickBooks invoices for one seller-side connection, marked with what has gone. */
  sendable: (connectionId: string, signal?: AbortSignal) =>
    apiGet<SendableInvoiceDto[]>(
      `/partners/outbound/connections/${encodeURIComponent(connectionId)}/invoices`, signal),

  /** Sends ONE invoice. Idempotent per (connection, invoice): a second press sends nothing. */
  send: (connectionId: string, qboInvoiceId: string, signal?: AbortSignal) =>
    apiPost<PartnerOutboundDeliveryDto>(
      `/partners/outbound/connections/${encodeURIComponent(connectionId)}/send`, { qboInvoiceId }, signal),

  /** Re-attempts a Failed or Refused send under the same canonical id. */
  retry: (id: string, signal?: AbortSignal) =>
    apiPost<PartnerOutboundDeliveryDto>(`/partners/outbound/${encodeURIComponent(id)}/retry`, {}, signal),
};

// ---------------------------------------------------------------------------
// Platform parity
// ---------------------------------------------------------------------------

/** This company's standing with the iERP platform, and the ledger of platform messages. */
export const platform = {
  state: (signal?: AbortSignal) => apiGet<PlatformStateDto>('/platform/state', signal),
  deliveries: (
    params: { type?: string | null; status?: string | null; page: number; pageSize: number },
    signal?: AbortSignal,
  ) =>
    apiGet<PagedResult<PlatformDeliveryDto>>(
      `/platform/deliveries${buildQuery({
        type: params.type ?? null,
        status: params.status ?? null,
        page: params.page,
        pageSize: params.pageSize,
      })}`,
      signal,
    ),
  refreshAssignment: (signal?: AbortSignal) =>
    apiPost<{ success: boolean; changed: boolean; assignedEnvironment: string | null; effectiveBaseUrl: string | null; message: string | null }>(
      '/platform/assignment/refresh', {}, signal),
  repairCredential: (signal?: AbortSignal) =>
    apiPost<{ success: boolean; message: string | null }>('/platform/credential/repair', {}, signal),
  refreshHealth: (signal?: AbortSignal) =>
    apiPost<{ success: boolean; message: string | null }>('/platform/health/refresh', {}, signal),
};

/** The licence purchase ledger under Settings. */
export const licencePurchases = {
  list: (signal?: AbortSignal) => apiGet<LicensePurchaseDto[]>('/licensing/purchases', signal),
  refresh: (licensePurchaseId: string, signal?: AbortSignal) =>
    apiPost<LicensePurchaseDto>(`/licensing/purchases/${encodeURIComponent(licensePurchaseId)}/refresh`, {}, signal),
  paymentLink: (licensePurchaseId: string, signal?: AbortSignal) =>
    apiPost<LicensePurchaseDto>(`/licensing/purchases/${encodeURIComponent(licensePurchaseId)}/payment-link`, {}, signal),
};

/** The payment leg of the invoice exchange. */
export const partnerPayments = {
  list: (
    params: { direction?: string | null; status?: string | null; connectionId?: string | null; page: number; pageSize: number },
    signal?: AbortSignal,
  ) =>
    apiGet<PagedResult<PartnerPaymentNotificationDto>>(
      `/partners/payments${buildQuery({
        direction: params.direction ?? null,
        status: params.status ?? null,
        connectionId: params.connectionId ?? null,
        page: params.page,
        pageSize: params.pageSize,
      })}`,
      signal,
    ),
  scan: (signal?: AbortSignal) => apiPost<PartnerPaymentScanResponse>('/partners/payments/scan', {}, signal),
};

// ---------------------------------------------------------------------------
// Setup Assistant
// ---------------------------------------------------------------------------

export const assistant = {
  context: (signal?: AbortSignal) => apiGet<AssistantContextDto>('/assistant/context', signal),
  ask: (body: AssistantAskRequest, signal?: AbortSignal) => apiPost<AssistantReplyDto>('/assistant/ask', body, signal),
};

// ---------------------------------------------------------------------------
// AI Learning — historical evidence acquisition, attachments, publication
// ---------------------------------------------------------------------------

/** The one-call status read the AI Learning screen is built around. 404s with NoConnectorIdentity
 * when QuickBooks has not been connected yet. */
export const learningDiagnostics = {
  get: (signal?: AbortSignal) => apiGet<LearningDiagnostics>('/learning/diagnostics', signal),
};

/** Historical transaction evidence: the resumable backfill, incremental sync, and the deletion sweep. */
export const learningEvidence = {
  /** Continues (or starts) the resumable backfill. Omit entityType to run all five types in order. */
  pull: (body: EvidencePullApiRequest = {}, signal?: AbortSignal) =>
    apiPost<EvidencePullApiResponse[]>('/learning/evidence/pull', body, signal),

  /** Incremental change sync; falls back to the backfill until it has completed. */
  sync: (body: EvidencePullApiRequest = {}, signal?: AbortSignal) =>
    apiPost<EvidencePullApiResponse[]>('/learning/evidence/sync', body, signal),

  /** CDC sweep across all entity types. Surfaces fullRescanRequired if deletion coverage has a gap. */
  syncDeletions: (signal?: AbortSignal) =>
    apiPost<EvidenceDeletionApiResponse>('/learning/evidence/sync-deletions', undefined, signal),
};

/** Historical attachments: cheap metadata discovery, then controlled byte retrieval. */
export const learningAttachments = {
  discover: (signal?: AbortSignal) =>
    apiPost<AttachmentDiscoveryApiResponse>('/learning/evidence/attachments/discover', undefined, signal),

  retrieve: (signal?: AbortSignal) =>
    apiPost<AttachmentRetrievalApiResponse>('/learning/evidence/attachments/retrieve', undefined, signal),
};

/** The governed publication surface: consent (admin-only) and publish (admin-only, licence-gated). */
export const learningPublication = {
  get: (signal?: AbortSignal) => apiGet<PublicationStateDto>('/learning/publication', signal),

  setConsent: (body: PublicationConsentApiRequest, signal?: AbortSignal) =>
    apiPost<PublicationStateDto>('/learning/publication/consent', body, signal),

  /** Synchronous — every batch is sent to iERP AI and awaited inline. Can be slow for a large history. */
  publish: (signal?: AbortSignal) =>
    apiPost<PublishApiResponse>('/learning/publication/publish', undefined, signal),
};

/**
 * AI Learning, scoped to ONE VENDOR at a time — mirroring the Business Central MultiEntity
 * ToolKit's own per-vendor "AI Learning Enabled" design. A vendor's history may not be published
 * until that vendor's invoice layout has been taught (a Vendor Intake for it approved), which the
 * publish call enforces server-side; there is nothing to pre-flight here.
 */
export const vendorLearning = {
  list: (signal?: AbortSignal) =>
    apiGet<VendorLearningSummaryDto[]>('/learning/vendors', signal),

  get: (vendorExternalId: string, signal?: AbortSignal) =>
    apiGet<VendorLearningDetailDto>(`/learning/vendors/${encodeURIComponent(vendorExternalId)}`, signal),

  setConsent: (vendorExternalId: string, body: VendorLearningConsentApiRequest, signal?: AbortSignal) =>
    apiPost<VendorLearningDetailDto>(
      `/learning/vendors/${encodeURIComponent(vendorExternalId)}/consent`, body, signal),

  /** Synchronous, scoped to this vendor's own posting evidence only. */
  publish: (vendorExternalId: string, signal?: AbortSignal) =>
    apiPost<VendorPublishApiResponse>(
      `/learning/vendors/${encodeURIComponent(vendorExternalId)}/publish`, undefined, signal),
};

// ---------------------------------------------------------------------------
// Email intake (forwarding tier)
// ---------------------------------------------------------------------------

export const emailIntake = {
  status: (signal?: AbortSignal) => apiGet<EmailIntakeStatusDto>('/email-intake/status', signal),

  activate: (body: ActivateEmailIntakeRequest, signal?: AbortSignal) =>
    apiPost<EmailIntakeStatusDto>('/email-intake/activate', body, signal),

  disable: (body: DisableEmailIntakeRequest, signal?: AbortSignal) =>
    apiPost<EmailIntakeStatusDto>('/email-intake/disable', body, signal),

  /** Syncs approved rules to the bot, asks it to check the mailbox, then imports what it found. */
  runNow: (signal?: AbortSignal) => apiPost<EmailIntakeRunNowResult>('/email-intake/run-now', undefined, signal),

  listRules: (signal?: AbortSignal) => apiGet<EmailIntakeApprovedRuleDto[]>('/email-intake/rules', signal),

  upsertRule: (body: UpsertEmailIntakeRuleRequest, signal?: AbortSignal) =>
    apiPost<EmailIntakeApprovedRuleDto>('/email-intake/rules', body, signal),

  deleteRule: (ruleId: string, signal?: AbortSignal) =>
    apiDelete<void>(`/email-intake/rules/${encodeURIComponent(ruleId)}`, signal),

  listForwarders: (signal?: AbortSignal) =>
    apiGet<EmailIntakeForwarderDto[]>('/email-intake/forwarders', signal),

  upsertForwarder: (body: UpsertEmailIntakeForwarderRequest, signal?: AbortSignal) =>
    apiPost<EmailIntakeForwarderDto>('/email-intake/forwarders', body, signal),

  deleteForwarder: (forwarderId: string, signal?: AbortSignal) =>
    apiDelete<void>(`/email-intake/forwarders/${encodeURIComponent(forwarderId)}`, signal),

  listPendingSenders: (signal?: AbortSignal) =>
    apiGet<EmailIntakePendingSenderDto[]>('/email-intake/pending-senders', signal),

  decidePendingSender: (pendingSenderId: string, body: DecideEmailIntakePendingSenderRequest, signal?: AbortSignal) =>
    apiPost<{ success: boolean }>(
      `/email-intake/pending-senders/${encodeURIComponent(pendingSenderId)}/decide`, body, signal),

  /** Invoices held because the document says they are addressed to another company. */
  listHeldInvoices: (signal?: AbortSignal) =>
    apiGet<EmailIntakeHeldInvoiceDto[]>('/email-intake/held-invoices', signal),

  /** Accept ("ours after all") or Reject ("not ours"). Admin only. */
  resolveHeldInvoice: (documentId: string, body: ResolveHeldInvoiceRequest, signal?: AbortSignal) =>
    apiPost<{ success: boolean }>(
      `/email-intake/held-invoices/${encodeURIComponent(documentId)}/resolve`, body, signal),

  /** Re-reads this company's name and address from QuickBooks, which is what arms the bill-to check. */
  refreshCompanyIdentity: (signal?: AbortSignal) =>
    apiPost<{ success: boolean; message: string }>(
      '/email-intake/refresh-company-identity', undefined, signal),

  /** Billing details that should also be accepted as this company. */
  listCompanyAliases: (signal?: AbortSignal) =>
    apiGet<EmailIntakeCompanyAliasDto[]>('/email-intake/company-aliases', signal),

  addCompanyAlias: (body: AddCompanyAliasRequest, signal?: AbortSignal) =>
    apiPost<EmailIntakeCompanyAliasDto>('/email-intake/company-aliases', body, signal),

  deleteCompanyAlias: (aliasId: string, signal?: AbortSignal) =>
    apiDelete<void>(`/email-intake/company-aliases/${encodeURIComponent(aliasId)}`, signal),

  /** Enforce / WarnOnly / Off. Admin only. */
  setCompanyMatchMode: (body: SetCompanyMatchModeRequest, signal?: AbortSignal) =>
    apiPost<{ success: boolean }>('/email-intake/company-match-mode', body, signal),
};

// ---------------------------------------------------------------------------
// Payables Agent
// ---------------------------------------------------------------------------

export const payablesAgent = {
  getSetup: (signal?: AbortSignal) => apiGet<PayablesAgentSetupDto>('/payables-agent/setup', signal),

  updateSetup: (body: UpdatePayablesAgentSetupRequest, signal?: AbortSignal) =>
    apiPut<PayablesAgentSetupDto>('/payables-agent/setup', body, signal),

  /** Capture -> profiles -> schedule -> refresh -> execute, the same five steps the background
   * worker runs on its own schedule. */
  runNow: (signal?: AbortSignal) => apiPost<PayablesAgentRunNowResult>('/payables-agent/run-now', undefined, signal),

  listScheduledPayments: (status: ScheduledPaymentStatus | null, signal?: AbortSignal) =>
    apiGet<ScheduledPaymentDto[]>(`/payables-agent/scheduled-payments${buildQuery({ status })}`, signal),

  reschedule: (id: string, newDateUtc: string, signal?: AbortSignal) =>
    apiPost<ScheduledPaymentDto>(`/payables-agent/scheduled-payments/${encodeURIComponent(id)}/reschedule`, { newDateUtc }, signal),

  confirm: (id: string, signal?: AbortSignal) =>
    apiPost<ScheduledPaymentDto>(`/payables-agent/scheduled-payments/${encodeURIComponent(id)}/confirm`, undefined, signal),

  hold: (id: string, reason: string | null, signal?: AbortSignal) =>
    apiPost<ScheduledPaymentDto>(`/payables-agent/scheduled-payments/${encodeURIComponent(id)}/hold`, { reason }, signal),

  release: (id: string, signal?: AbortSignal) =>
    apiPost<ScheduledPaymentDto>(`/payables-agent/scheduled-payments/${encodeURIComponent(id)}/release`, undefined, signal),

  cancel: (id: string, reason: string | null, signal?: AbortSignal) =>
    apiPost<ScheduledPaymentDto>(`/payables-agent/scheduled-payments/${encodeURIComponent(id)}/cancel`, { reason }, signal),

  /** Creates the real QuickBooks payment immediately — the one call here that moves money without
   * an approval step in between. */
  payNow: (id: string, signal?: AbortSignal) =>
    apiPost<ScheduledPaymentDto>(`/payables-agent/scheduled-payments/${encodeURIComponent(id)}/pay-now`, undefined, signal),
  reconcile: (id: string, signal?: AbortSignal) =>
    apiPost<{ resolved: boolean; message: string; item: ScheduledPaymentDto }>(
      `/payables-agent/scheduled-payments/${encodeURIComponent(id)}/reconcile`, undefined, signal),

  approve: (id: string, signal?: AbortSignal) =>
    apiPost<ScheduledPaymentDto>(`/payables-agent/scheduled-payments/${encodeURIComponent(id)}/approve`, undefined, signal),

  reject: (id: string, reason: string | null, signal?: AbortSignal) =>
    apiPost<ScheduledPaymentDto>(`/payables-agent/scheduled-payments/${encodeURIComponent(id)}/reject`, { reason }, signal),

  listVendorProfiles: (signal?: AbortSignal) =>
    apiGet<VendorPaymentProfileDto[]>('/payables-agent/vendor-profiles', signal),

  /** Every vendor's place on the stage ladder, joined with its learned profile. */
  listVendorStages: (signal?: AbortSignal) =>
    apiGet<VendorStageRow[]>('/payables-agent/vendor-stages', signal),

  /** Moves a vendor exactly one rung up — never more, however this is called. */
  advanceVendorStage: (qboVendorId: string, note: string | null, signal?: AbortSignal) =>
    apiPost<VendorStageRow['override']>(
      `/payables-agent/vendor-stages/${encodeURIComponent(qboVendorId)}/advance`,
      { note } satisfies AdvanceVendorStageRequest, signal),

  /** The correction path — jump or demote a vendor directly, instead of one rung at a time. */
  setVendorStage: (qboVendorId: string, body: SetVendorStageRequest, signal?: AbortSignal) =>
    apiPost<VendorStageRow['override']>(
      `/payables-agent/vendor-stages/${encodeURIComponent(qboVendorId)}/set-stage`, body, signal),

  setVendorExclusion: (qboVendorId: string, body: SetVendorExclusionRequest, signal?: AbortSignal) =>
    apiPost<VendorStageRow['override']>(
      `/payables-agent/vendor-stages/${encodeURIComponent(qboVendorId)}/exclude`, body, signal),

  setVendorOverrideDays: (qboVendorId: string, body: SetVendorOverrideDaysRequest, signal?: AbortSignal) =>
    apiPost<VendorStageRow['override']>(
      `/payables-agent/vendor-stages/${encodeURIComponent(qboVendorId)}/override-days`, body, signal),

  getLog: (signal?: AbortSignal) => apiGet<PayablesAgentLogEntryDto[]>('/payables-agent/log', signal),
};
